import { Button } from 'antd';
import { FileSearch, type LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useGetAppliedJobsQuery } from '../../app/features/recruitment/recruitment.api';
import { useAppFilters } from '../../hooks';
import useAuth from '../../hooks/useAuth';
import cn from '../../lib/cn';
import { AppliedJobCard } from './components/AppliedJobCard';
import { JobCardSkeleton } from './components/JobCardSkeleton';
import { EmptyState, PortalPagination, PortalShell } from './components/portal';
import { ALL_STAGES_ICON, STAGES, type StageValue } from './components/stages';
import { PATHS } from '../../routes/paths';

/** Pipeline overview tile — shows the stage count and filters by it. */
const StageTile = ({
  stage,
  label,
  icon: Icon,
  dot,
  active,
  onSelect,
}: {
  stage?: StageValue;
  label: string;
  icon: LucideIcon;
  dot?: string;
  active: boolean;
  onSelect: () => void;
}) => {
  const { verifiedInfo } = useAuth();
  const { data, isLoading } = useGetAppliedJobsQuery(
    { applicant_id: verifiedInfo?.id as string, limit: 1, stage },
    { skip: !verifiedInfo?.id },
  );
  const count = data?.data?.meta?.total ?? 0;

  return (
    <button
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        'flex flex-col rounded-xl border p-4 text-left transition-colors',
        active
          ? 'border-theme-primary/50 bg-theme-primary/[0.04] ring-1 ring-theme-primary/20'
          : 'border-gray-200 bg-white hover:border-gray-300',
      )}
    >
      <span className="flex items-center gap-2 text-[13px] font-medium text-gray-500">
        <Icon
          className={cn(
            'h-4 w-4',
            active ? 'text-theme-primary' : 'text-gray-400',
          )}
          strokeWidth={1.75}
        />
        {label}
      </span>
      <span className="mt-3 flex items-center gap-2">
        <span className="text-2xl font-semibold tabular-nums tracking-tight text-gray-900">
          {isLoading ? '–' : count}
        </span>
        {dot && count > 0 && (
          <span className={cn('h-1.5 w-1.5 rounded-full', dot)} />
        )}
      </span>
    </button>
  );
};

const AppliedJobs = () => {
  const navigate = useNavigate();
  const { verifiedInfo } = useAuth();
  const { limit, skip, page, filters, handleFilter, handlePagination } =
    useAppFilters('applied-jobs-filters');
  const activeStage = filters.stage as StageValue | undefined;

  const { data, isLoading, isFetching } = useGetAppliedJobsQuery(
    {
      skip,
      limit,
      stage: activeStage,
      applicant_id: verifiedInfo?.id as string,
    },
    { skip: !verifiedInfo?.id },
  );

  const appliedJobs = data?.data?.data || [];
  const total = data?.data?.meta?.total || 0;
  const loading = isLoading || isFetching;

  return (
    <PortalShell
      tab="applied"
      title="Applied jobs"
      description="Track every application from screening to offer, all in one place."
    >
      <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StageTile
          label="All applications"
          icon={ALL_STAGES_ICON}
          active={!activeStage}
          onSelect={() => handleFilter('stage', undefined)}
        />
        {STAGES.map((stage) => (
          <StageTile
            key={stage.value}
            stage={stage.value}
            label={stage.label}
            icon={stage.icon}
            dot={stage.dot}
            active={activeStage === stage.value}
            onSelect={() => handleFilter('stage', stage.value)}
          />
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <JobCardSkeleton key={index} />
          ))}
        </div>
      ) : appliedJobs.length > 0 ? (
        <div className="space-y-3">
          {appliedJobs.map((job) => (
            <AppliedJobCard key={job.job_application_id} job={job} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={FileSearch}
          title={
            activeStage
              ? 'No applications at this stage'
              : 'No applications yet'
          }
          description="When you apply for a job, you can track its progress here."
          action={
            <Button
              onClick={() => navigate(PATHS.jobPortal.root)}
              className="btn-primary h-9 rounded-full px-5 font-medium"
            >
              Browse jobs
            </Button>
          }
        />
      )}

      <PortalPagination
        page={page}
        limit={limit}
        total={total}
        onChange={handlePagination}
      />
    </PortalShell>
  );
};

export default AppliedJobs;
